/**
 * Value-first grouping for a condition's per-country / per-campaign overrides.
 *
 * The rules list used to append every pair inline — `(australia:50,
 * hungary:50, poland:100, …)` — which past three countries reads as noise and
 * buries the base threshold. Grouping by VALUE inverts it into the shape the
 * author actually thinks in: threshold tiers, each with the countries that use
 * it. Leaf module (no constants/hooks imports) like format-overrides, so specs
 * can import it without the VOLUUM_COLUMNS barrel cycle.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ConditionOverrides } from '../format-overrides';

export interface OverrideValueGroup {
  value: number;
  /** Pretty country names, alphabetical. */
  countries: string[];
  /** Shortened campaign ids (`▸` prefix is the renderer's job). */
  campaigns: string[];
}

/** `united_kingdom` → `United Kingdom`, `belgium_nl` → `Belgium NL`. */
export function prettyCountryKey(key: string): string {
  return key
    .split('_')
    .filter(Boolean)
    .map((t) => (t.length <= 2 ? t.toUpperCase() : t[0].toUpperCase() + t.slice(1)))
    .join(' ');
}

const shortCampaign = (id: string): string =>
  id.length > 10 ? `${id.slice(0, 8)}…` : id;

export function overrideCount(c: ConditionOverrides): number {
  const countries = (c.countryThresholds ?? []).filter((t) => t.countryKey?.trim()).length;
  const campaigns = (c.campaignThresholds ?? []).filter((t) => t.campaignId?.trim()).length;
  return countries + campaigns;
}

/** Ascending by value — reads as tiers, lowest bar first. */
export function groupOverrides(c: ConditionOverrides): OverrideValueGroup[] {
  const byValue = new Map<number, OverrideValueGroup>();
  const groupFor = (value: number): OverrideValueGroup => {
    let g = byValue.get(value);
    if (!g) {
      g = { value, countries: [], campaigns: [] };
      byValue.set(value, g);
    }
    return g;
  };
  for (const t of c.countryThresholds ?? []) {
    if (t.countryKey?.trim()) groupFor(t.value).countries.push(prettyCountryKey(t.countryKey));
  }
  for (const t of c.campaignThresholds ?? []) {
    if (t.campaignId?.trim()) groupFor(t.value).campaigns.push(shortCampaign(t.campaignId));
  }
  const groups = [...byValue.values()];
  for (const g of groups) g.countries.sort((a, b) => a.localeCompare(b));
  return groups.sort((a, b) => a.value - b.value);
}
